/**
 * Desktop notifications for the agent, shown while the dashboard is open.
 *
 * This is the *foreground* half of the notification story and is deliberately
 * separate from Web Push:
 *
 *   - Web Push (`push.js` + `sw.js`) reaches the agent when the dashboard is
 *     closed. The server decides when to send it.
 *   - This module fires when the tab is open but not being looked at. The
 *     server would skip a push in that case, because a socket is attached, so
 *     without this a backgrounded dashboard would go quiet.
 *
 * Both end up rendering through the same service worker registration when one
 * exists, so notifications stack and replace by `tag` consistently.
 */

import { notificationRoute } from '@superchat/shared/notifications';

const SETTING_KEY = 'superchat:desktopNotifications';

export const supported = () => typeof window !== 'undefined' && 'Notification' in window;

export const permission = () => (supported() ? Notification.permission : 'unsupported');

/** Agent-level switch, independent of the per-visitor mute. */
export function desktopEnabled() {
  if (!supported() || Notification.permission !== 'granted') return false;
  return localStorage.getItem(SETTING_KEY) !== 'false';
}

export function setDesktopEnabled(enabled) {
  localStorage.setItem(SETTING_KEY, String(enabled));
}

export async function requestPermission() {
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  // Must be called from a user gesture in Firefox and Safari.
  return Notification.requestPermission();
}

/**
 * Shows a notification unless the tab is already visible — if the agent is
 * looking at the dashboard, the in-app toast is enough and a duplicate OS
 * banner is just noise.
 *
 * `force` bypasses that for emergencies, which should interrupt regardless.
 */
export function notify({
  title,
  body,
  tag,
  appId,
  conversationId,
  ticketId,
  kind,
  force = false,
  requireInteraction = false,
}) {
  if (!desktopEnabled()) return null;
  if (document.visibilityState === 'visible' && !force) return null;

  const options = {
    body,
    tag,
    renotify: force,
    requireInteraction,
    /**
     * The app id belongs here as much as the conversation does.
     *
     * This carried only `conversationId`, which is half an address: an agency
     * console holds several tenants, and a conversation id means nothing until
     * the right one is selected. A click resolved against whichever app
     * happened to be in focus, so a banner about merchant B opened an empty
     * thread in merchant A.
     *
     * The service worker reads this same object on click, so both paths below
     * end up with the whole address.
     */
    data: { appId, conversationId, ticketId, kind },
  };

  try {
    // Prefer the service worker: notifications survive the page and the click
    // handler in sw.js focuses the right tab.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) registration.showNotification(title, options);
        else showDirect(title, options);
      });
      return true;
    }
    return showDirect(title, options);
  } catch {
    return null;
  }
}

/**
 * The no-service-worker fallback.
 *
 * Its click cannot be handled by `sw.js`, so it routes itself — through the
 * same `notificationRoute` the worker uses, so the two cannot drift into
 * sending the same banner to different places.
 */
function showDirect(title, options) {
  const notification = new Notification(title, options);
  notification.onclick = () => {
    window.focus();
    window.location.assign(notificationRoute(options.data ?? {}));
    notification.close();
  };
  return notification;
}

export default { notify, supported, permission, requestPermission, desktopEnabled, setDesktopEnabled };
